import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type MirrorSurface = "avatar" | "energy";

// Cache de sessão SPA (mesmo espírito do useAvatar): evita flash da
// superfície default em re-entradas no Espelho para quem escolheu a
// não-default. Guardar o userId impede vazar a escolha entre contas num
// logout→login sem reload.
let cached: { userId: string; choice: MirrorSurface | null } | null = null;

function isMirrorSurface(v: unknown): v is MirrorSurface {
  return v === "avatar" || v === "energy";
}

interface UseMirrorSurfaceReturn {
  surface: MirrorSurface;
  setSurface: (next: MirrorSurface) => void;
  loading: boolean;
}

/**
 * Preferência avatar × energia do Espelho (user_profiles.mirror_surface).
 *
 * NULL no banco = "nunca escolheu" e resolve para o comportamento de sempre:
 * tem avatar → avatar; não tem → energia. Enquanto carrega vale a MESMA
 * resolução, então quem nunca escolheu nunca vê nada diferente de hoje.
 * `hasAvatar` vem do useAvatar do chamador — este hook não refaz essa busca.
 *
 * setSurface é otimista: troca na hora e persiste em background; falha de
 * persistência (inclusive UPDATE que não casou linha — perfil ainda não
 * existe) loga com console.error e REVERTE o estado local.
 */
export function useMirrorSurface(hasAvatar: boolean): UseMirrorSurfaceReturn {
  // Otimista a partir do cache; o effect valida o dono e corrige se preciso.
  const [choice, setChoice] = useState<MirrorSurface | null>(cached?.choice ?? null);
  const [loading, setLoading] = useState(cached === null);

  const choiceRef = useRef(choice);
  choiceRef.current = choice;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          // Sem sessão ainda: resolve por hasAvatar (default de hoje) e NÃO
          // cacheia, para a próxima montagem tentar de novo.
          if (!cancelled) setLoading(false);
          return;
        }
        if (cached && cached.userId === user.id) {
          if (!cancelled) {
            setChoice(cached.choice);
            setLoading(false);
          }
          return;
        }
        const { data, error } = await supabase
          .from("user_profiles")
          .select("mirror_surface")
          .eq("user_id", user.id)
          .maybeSingle();
        if (error) throw error;
        const stored = isMirrorSurface(data?.mirror_surface) ? data.mirror_surface : null;
        cached = { userId: user.id, choice: stored };
        if (!cancelled) {
          setChoice(stored);
          setLoading(false);
        }
      } catch (err) {
        // Fallback = default por hasAvatar. Para quem ESCOLHEU a superfície
        // não-default isso ignora a escolha — mente sobre a preferência —
        // então é error, não warn.
        console.error("[useMirrorSurface] load failed:", err);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setSurface = useCallback((next: MirrorSurface) => {
    const prev = choiceRef.current;
    if (next === prev) return;
    // Otimista: a tela troca já; a persistência corre atrás.
    setChoice(next);
    if (cached) cached = { ...cached, choice: next };
    void (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("no session");
        cached = { userId: user.id, choice: next };
        const { data, error } = await supabase
          .from("user_profiles")
          .update({ mirror_surface: next })
          .eq("user_id", user.id)
          .select("mirror_surface");
        if (error) throw error;
        // Regra 4.9: UPDATE que casou 0 linhas é falha silenciosa — trate
        // como erro para o revert abaixo acontecer.
        if (!data || data.length === 0) throw new Error("update matched 0 rows");
      } catch (err) {
        console.error("[useMirrorSurface] persist failed — reverting:", err);
        if (cached) cached = { ...cached, choice: prev };
        setChoice(prev);
      }
    })();
  }, []);

  const surface: MirrorSurface = choice ?? (hasAvatar ? "avatar" : "energy");

  return { surface, setSurface, loading };
}
